/**
 * Accordion Component
 *
 * Componente accordion baseado em shadcn/ui com @radix-ui/react-accordion.
 * Suporta single/multiple expand, animacoes suaves, dark mode via CSS variables,
 * e diferentes estilos visuais.
 *
 * @since 2025-12-16
 * @updated 2026-04-06 - Migrado para shadcn/ui (Radix Accordion + cn())
 * @epic INIT-0046 Epic 0002
 */

import * as React from 'react';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ==========================================
// TYPES
// ==========================================

export interface AccordionItem {
  key: string;
  title: React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}

type AccordionVariant = 'default' | 'bordered' | 'separated';

// ==========================================
// PRIMITIVE SHADCN COMPONENTS
// ==========================================

const AccordionRoot = RadixAccordion.Root;

const AccordionItemPrimitive = React.forwardRef<
  React.ElementRef<typeof RadixAccordion.Item>,
  React.ComponentPropsWithoutRef<typeof RadixAccordion.Item>
>(({ className, ...props }, ref) => (
  <RadixAccordion.Item
    ref={ref}
    className={cn('border-b border-gray-200 dark:border-dark-border', className)}
    {...props}
  />
));
AccordionItemPrimitive.displayName = 'AccordionItemPrimitive';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof RadixAccordion.Trigger>,
  React.ComponentPropsWithoutRef<typeof RadixAccordion.Trigger> & {
    icon?: React.ReactNode;
  }
>(({ className, children, icon, ...props }, ref) => (
  <RadixAccordion.Header className="flex">
    <RadixAccordion.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all',
        'text-gray-900 dark:text-dark-text',
        'hover:underline',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        '[&[data-state=open]>svg]:rotate-180',
        '[&[data-state=open]>.accordion-chevron]:rotate-180',
        className
      )}
      {...props}
    >
      <span className="flex items-center gap-3 text-left">
        {icon && (
          <span className="w-5 h-5 text-gray-500 dark:text-dark-text-secondary shrink-0">
            {icon}
          </span>
        )}
        {children}
      </span>
      <ChevronDown
        className="accordion-chevron h-4 w-4 shrink-0 text-gray-500 dark:text-dark-text-secondary transition-transform duration-200"
        aria-hidden="true"
      />
    </RadixAccordion.Trigger>
  </RadixAccordion.Header>
));
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof RadixAccordion.Content>,
  React.ComponentPropsWithoutRef<typeof RadixAccordion.Content>
>(({ className, children, ...props }, ref) => (
  <RadixAccordion.Content
    ref={ref}
    className={cn(
      'overflow-hidden text-sm',
      'data-[state=closed]:animate-accordion-up',
      'data-[state=open]:animate-accordion-down'
    )}
    {...props}
  >
    <div
      className={cn(
        'pb-4 text-gray-600 dark:text-dark-text-secondary',
        className
      )}
    >
      {children}
    </div>
  </RadixAccordion.Content>
));
AccordionContent.displayName = 'AccordionContent';

// ==========================================
// VARIANT STYLES
// ==========================================

const variantContainerStyles: Record<AccordionVariant, string> = {
  default: 'divide-y divide-gray-200 dark:divide-dark-border',
  bordered: 'border border-gray-200 dark:border-dark-border rounded-lg divide-y divide-gray-200 dark:divide-dark-border',
  separated: 'space-y-2',
};

const variantItemStyles: Record<AccordionVariant, string> = {
  default: '',
  bordered: '',
  separated: 'border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden bg-white dark:bg-dark-card',
};

const variantTriggerStyles: Record<AccordionVariant, string> = {
  default: 'py-4 px-0',
  bordered: 'py-4 px-4',
  separated: 'py-4 px-4',
};

const variantContentStyles: Record<AccordionVariant, string> = {
  default: 'pb-4',
  bordered: 'px-4 pb-4',
  separated: 'px-4 pb-4',
};

// ==========================================
// HIGH-LEVEL ACCORDION COMPONENT (items prop)
// ==========================================

interface AccordionProps {
  items: AccordionItem[];
  defaultExpandedKeys?: string[];
  expandedKeys?: string[];
  onChange?: (keys: string[]) => void;
  multiple?: boolean;
  variant?: AccordionVariant;
  className?: string;
}

export function Accordion({
  items,
  defaultExpandedKeys = [],
  expandedKeys: controlledExpandedKeys,
  onChange,
  multiple = false,
  variant = 'default',
  className,
}: AccordionProps) {
  const isControlled = controlledExpandedKeys !== undefined;
  const type = multiple ? 'multiple' : 'single';

  const handleValueChange = (value: string | string[]) => {
    if (!onChange) return;
    onChange(Array.isArray(value) ? value : value ? [value] : []);
  };

  const containerClass = cn(variantContainerStyles[variant], className);

  if (multiple) {
    return (
      <AccordionRoot
        type="multiple"
        defaultValue={isControlled ? undefined : defaultExpandedKeys}
        value={isControlled ? controlledExpandedKeys : undefined}
        onValueChange={isControlled ? (v) => handleValueChange(v) : undefined}
        className={containerClass}
      >
        {items.map((item) => (
          <AccordionItemPrimitive
            key={item.key}
            value={item.key}
            disabled={item.disabled}
            className={cn(variantItemStyles[variant], 'border-b-0')}
          >
            <AccordionTrigger
              icon={item.icon}
              className={variantTriggerStyles[variant]}
            >
              {item.title}
            </AccordionTrigger>
            <AccordionContent className={variantContentStyles[variant]}>
              {item.content}
            </AccordionContent>
          </AccordionItemPrimitive>
        ))}
      </AccordionRoot>
    );
  }

  // Single mode
  return (
    <AccordionRoot
      type="single"
      collapsible
      defaultValue={isControlled ? undefined : defaultExpandedKeys[0]}
      value={isControlled ? (controlledExpandedKeys[0] ?? '') : undefined}
      onValueChange={isControlled ? (v) => handleValueChange(v) : undefined}
      className={containerClass}
    >
      {items.map((item) => (
        <AccordionItemPrimitive
          key={item.key}
          value={item.key}
          disabled={item.disabled}
          className={cn(variantItemStyles[variant], 'border-b-0')}
        >
          <AccordionTrigger
            icon={item.icon}
            className={variantTriggerStyles[variant]}
          >
            {item.title}
          </AccordionTrigger>
          <AccordionContent className={variantContentStyles[variant]}>
            {item.content}
          </AccordionContent>
        </AccordionItemPrimitive>
      ))}
    </AccordionRoot>
  );
}

// ==========================================
// COMPOUND COMPONENTS (AccordionRoot + AccordionPanel)
// ==========================================

interface AccordionRootProps {
  children: React.ReactNode;
  multiple?: boolean;
  defaultExpandedKeys?: string[];
  onChange?: (keys: string[]) => void;
  variant?: AccordionVariant;
  className?: string;
}

export function AccordionRootCompound({
  children,
  multiple = false,
  defaultExpandedKeys = [],
  onChange,
  variant = 'default',
  className,
}: AccordionRootProps) {
  const containerClass = cn(variantContainerStyles[variant], className);

  const handleValueChange = (value: string | string[]) => {
    if (!onChange) return;
    onChange(Array.isArray(value) ? value : value ? [value] : []);
  };

  // Provide variant context to children via wrapper
  const contextValue = React.useMemo(() => ({ variant }), [variant]);

  const content = (
    <AccordionVariantContext.Provider value={contextValue}>
      {children}
    </AccordionVariantContext.Provider>
  );

  if (multiple) {
    return (
      <AccordionRoot
        type="multiple"
        defaultValue={defaultExpandedKeys}
        onValueChange={(v) => handleValueChange(v)}
        className={containerClass}
      >
        {content}
      </AccordionRoot>
    );
  }

  return (
    <AccordionRoot
      type="single"
      collapsible
      defaultValue={defaultExpandedKeys[0]}
      onValueChange={(v) => handleValueChange(v)}
      className={containerClass}
    >
      {content}
    </AccordionRoot>
  );
}

// Context for variant
const AccordionVariantContext = React.createContext<{ variant: AccordionVariant }>({
  variant: 'default',
});

// Re-export as AccordionRoot for backward compat
export { AccordionRootCompound as AccordionRoot };

// ==========================================
// ACCORDION PANEL (compound child)
// ==========================================

interface AccordionPanelProps {
  itemKey: string;
  title: React.ReactNode;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export function AccordionPanel({
  itemKey,
  title,
  children,
  icon,
  disabled,
}: AccordionPanelProps) {
  const { variant } = React.useContext(AccordionVariantContext);

  return (
    <AccordionItemPrimitive
      value={itemKey}
      disabled={disabled}
      className={cn(variantItemStyles[variant], 'border-b-0')}
    >
      <AccordionTrigger
        icon={icon}
        className={variantTriggerStyles[variant]}
      >
        {title}
      </AccordionTrigger>
      <AccordionContent className={variantContentStyles[variant]}>
        {children}
      </AccordionContent>
    </AccordionItemPrimitive>
  );
}

// ==========================================
// EXPORTS (shadcn primitives para uso avancado)
// ==========================================

export {
  AccordionRoot as AccordionPrimitive,
  AccordionItemPrimitive,
  AccordionTrigger,
  AccordionContent,
};
